import httpClient from '@/httpClient/httpClient';
import { ActionTree } from 'vuex';
import { Notification } from 'uiv';
import i18n from '@/i18n';
import { ResponseBody, RootState, TRequestData, ResponseData, ResponseDataProvider } from '@/types/index';
import { ICampaignGroupsState, ICampaignGroup } from '@/types/campaign/groups';
import ResponseDataValue, { ResponseDataProviderValue } from '@/utils/ResponseDataValue';
import {cloneDeep} from 'lodash';
import { setGroupErrorsFromResponseData, validateGroupForm } from './utils/validateGroupForm';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');
const DELETE_SUCCESS = i18n.t('notifications.deleteSuccess');
const IdPostfix = 'new';
const HOME_PAGE = '/groups'

function createNewId(): string {
  const id: number = Date.now();
  return `${id}-${IdPostfix}`.toString();
}

const actions: ActionTree<ICampaignGroupsState, RootState> = {
  setGroup({ commit, getters }, group) {
    const groupInit = cloneDeep(getters.getGroupInit);

    const newGroup = {
      id: group.id.toString(),
      name: group.name || groupInit.name,
      comment: group.comment || groupInit.comment,
      // TODO Будет добавлено позже
      // startDate: group.start_date || groupInit.startDate,
      // endDate: group.end_time || groupInit.endTime,
      // timeZone: group.time_zone || groupInit.timeZone,
      // budgetType: group.budget_type || groupInit.budgetType,
      // amount: group.amount || groupInit.amount,
      // goal: group.goal || groupInit.goal,
      // total: group.total || groupInit.total,
      // daily: group.daily || groupInit.daily,
      // spend: group.spend || groupInit.spend,
      // evenly: group.evenly || groupInit.evenly,
      // asap: group.asap || groupInit.asap,
      status: group.status || groupInit.status,
      errors: groupInit.errors
    }

    commit('setGroup', newGroup);
  },

  addGroup({commit, getters}) {
    const newGroup: ICampaignGroup = cloneDeep(getters.getGroupInit)
    newGroup.id = createNewId();

    commit('setGroup', newGroup);
  },
  
  changeGroup({commit}, payload) {
    commit('changeGroup', payload);
  },

  clearGroup({commit}) {
    commit('clearGroup');
  },

  async submitForm({ commit, getters, dispatch }, submitData) {
    const group = getters.getGroup;
    const isValidGroup = validateGroupForm(group);

    if (!isValidGroup) {
      Notification.notify.warning({
        title: i18n.t('notifications.checkForm'),
        duration: 3000,
      });
      return;
    }

    commit('changeIsSavingForm', true);
    const formData = new FormData();

    if (group.id && !group.id.includes(IdPostfix)) {
      formData.append('id', group.id);
    }
    if (group.name) {
      formData.append('name', group.name);
    }
    if (group.comment) {
      formData.append('comment', group.comment);
    }
    // TODO: Будет добавлено позже
    // if (group.startDate) {
    //   formData.append('start_date', group.startDate);
    // }
    // if (group.endDate) {
    //   formData.append('end_date', group.endDate);
    // }
    // if (group.timeZone) {
    //   formData.append('time_zone', group.timeZone);
    // }
    // if (group.budgetType) {
    //   formData.append('budget_type', group.budgetType);
    // }
    // if (group.amount) {
    //   formData.append('amount', group.amount);
    // }
    // if (group.goal) {
    //   formData.append('goal', group.goal.toString());

    //   if (group.total) {
    //     formData.append('total', group.total);
    //   }
    //   if (group.daily) {
    //     formData.append('daily', group.daily);
    //   }
    // }
    // if (group.spend) {
    //   formData.append('spend', group.spend.toString());

    //   if (group.evenly) {
    //     formData.append('evenly', group.evenly);
    //   }
    //   if (group.asap) {
    //     formData.append('asap', group.asap);
    //   }
    // }
    // if (group.status?.value) {
    //   formData.append('status', group.status.value);
    // }

    const { APIName, groupId, backUrl } = submitData;
    const requestData: TRequestData = {
      formData,
    };
    if (groupId) {
      requestData.id = groupId;
    }

    const response: ResponseData = await dispatch(APIName, requestData);

    if (response?.success) {
      window.location.href = backUrl ?? HOME_PAGE;
    } else {
      const errors = response?.data;
      setGroupErrorsFromResponseData(errors, group);
    }

    commit('changeIsSavingForm', false);
  },

  // ******************************
  // API
  async loadGroups({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/campaign/group', {
        params: Object.assign({}, data.searchData, {
          fields: data.columns.join(',')
        }) 
      });

      const responseDataProvider: ResponseDataProvider = ResponseDataProviderValue(response, true);

      commit('setGroups', responseDataProvider.data);
      return responseDataProvider;
    } catch (err) {
      Notification.notify.error({
        title: REQUEST_ERROR
      })
    }
  },

  async loadGroup({ dispatch }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/group/' + data.id,
        {
          params: data,
        }
      );
      dispatch('setGroup', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },

  async createGroup(_, requestData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/campaign/group',
        requestData.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },

  async deleteGroup({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/campaign/group/' + data.id,
        {
          params: data,
        }
      );

      commit('deleteGroup', data.id);
      Notification.notify.success({
        title: DELETE_SUCCESS,
      });
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },

  async updateGroup(_, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/campaign/group/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },

  async cloneGroup(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/campaign/group/clone/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
}

export default actions;
